import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CandidateEvaluation.css';

const EXAM_API = process.env.REACT_APP_EXAM_API_URL || `${process.env.REACT_APP_API_URL || '/api'}/exam`;

/** Hint tooltip — hover to see explanation */
const Hint = ({ text }) => (
  <span className="eval-hint" aria-label={text} data-tooltip={text}>
    ⓘ
  </span>
);

export default function CandidateEvaluation() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [searched, setSearched] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${EXAM_API}/evaluation/candidates?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setRecentCandidates(data.candidates || []);
      }
    } catch (err) {
      console.error('Failed to load candidates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!email) return;
    setSearched(true);
    try {
      const res = await fetch(`${EXAM_API}/evaluation/candidate/${encodeURIComponent(email.toLowerCase())}`);
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          // Compute overall result from session verdicts
          // PASS only if all 3 sessions completed AND all verdicts are PASS
          const verdicts = (data.sessions || [])
            .map(s => s.llm_evaluation?.verdict)
            .filter(Boolean);
          let overall_result = 'Pending';
          if (data.sessions_completed >= 3 && verdicts.length >= 3 && verdicts.every(v => v === 'PASS')) {
            overall_result = 'Pass';
          } else if (verdicts.length > 0) {
            overall_result = 'Fail';
          }

          setSearchResult({
            name: data.candidate_name,
            email: data.email,
            process: data.process,
            sessions_completed: data.sessions_completed,
            total_time_seconds: data.total_time_seconds,
            overall_result,
          });
        } else {
          setSearchResult(null);
        }
      }
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResult(null);
    }
  };

  const handleViewResult = (candidateEmail) => {
    navigate(`/admin/evaluation/result?email=${encodeURIComponent(candidateEmail)}`);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins} mins`;
  };

  return (
    <div className="eval-content">
      <div className="eval-header-row">
        <h2 className="page-title">Candidate Evaluation</h2>
      </div>

      {/* Search bar */}
      <div className="eval-search-bar">
        <div className="eval-input-wrapper">
          <label htmlFor="eval-email">Email ID</label>
          <input
            id="eval-email"
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setSearched(false); setSearchResult(null); }}
            placeholder="Enter candidate email"
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            required
          />
        </div>
        <button
          className="btn-view-result"
          onClick={handleSearch}
          disabled={!email}
        >
          Search
        </button>
      </div>

      {/* Search result */}
      {searched && (
        <div className="eval-results-table-wrapper">
          <table className="eval-results-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Candidate Name</th>
                <th>Email ID</th>
                <th>Process</th>
                <th>Sessions <Hint text="Number of scenario sessions the candidate has completed" /></th>
                <th>Duration <Hint text="Total time spent across all sessions combined" /></th>
                <th>Result <Hint text="Pass only if all 3 scenarios are passed. Fail if any scenario is failed" /></th>
                <th>View Result</th>
              </tr>
            </thead>
            <tbody>
              {searchResult ? (
                <tr>
                  <td>1</td>
                  <td>{searchResult.name}</td>
                  <td>{searchResult.email}</td>
                  <td>{searchResult.process}</td>
                  <td>{searchResult.sessions_completed}</td>
                  <td>{formatDuration(searchResult.total_time_seconds)}</td>
                  <td>
                    <span className={`eval-result-badge eval-result-${searchResult.overall_result.toLowerCase()}`}>
                      {searchResult.overall_result}
                    </span>
                  </td>
                  <td>
                    <button className="btn-view-result-table" onClick={() => handleViewResult(searchResult.email)}>
                      View Result
                    </button>
                  </td>
                </tr>
              ) : (
                <tr><td colSpan="8" className="eval-empty">No evaluation found for this candidate.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Candidates */}
      <div className="eval-results-table-wrapper" style={{ marginTop: '24px' }}>
        <table className="eval-results-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Candidate Name</th>
              <th>Email ID</th>
              <th>Process</th>
              <th>Sessions <Hint text="Number of scenario sessions the candidate has completed" /></th>
              <th>Duration <Hint text="Total time spent across all sessions combined" /></th>
              <th>Result <Hint text="Pass only if all 3 scenarios are passed. Fail if any scenario is failed" /></th>
              <th>View Result</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="eval-empty">Loading...</td></tr>
            ) : recentCandidates.length === 0 ? (
              <tr><td colSpan="8" className="eval-empty">No evaluations found.</td></tr>
            ) : (
              recentCandidates.map((c, idx) => (
                <tr key={c.email}>
                  <td>{idx + 1}</td>
                  <td>{c.candidate_name}</td>
                  <td>{c.email}</td>
                  <td>{c.process}</td>
                  <td>{c.sessions_completed}</td>
                  <td>{formatDuration(c.total_time_seconds)}</td>
                  <td>
                    <span className={`eval-result-badge eval-result-${(c.overall_result || 'pending').toLowerCase()}`}>
                      {c.overall_result || 'Pending'}
                    </span>
                  </td>
                  <td>
                    <button className="btn-view-result-table" onClick={() => handleViewResult(c.email)}>
                      View Result
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
